import React, { memo } from 'react';
import styled from 'styled-components';
import { BodyText, Flex } from '@kvdbil/components';
import AlternativePaymentInformationBuyNow from './AlternativePaymentInformationBuyNow';
import Auction, { AuctionType } from '~/App/shared/interfaces/Auction';
import { useTranslation } from '~/Locale';
import { getReducedPrice } from '~/helpers/auction';
import { inclVATtoExclVAT } from '~/helpers/currency';
import { getAllowedResidualLoan } from '~/helpers/calculator';
import { PriceInEUR } from './PriceInEUR';
import { BuyMethodPriceLabelValue } from './BuyMethodPriceLabelValue';
import { isHeavyEquipmentObject } from '../helpers';
import useShowExclVat from '~/App/shared/hooks/useShowExclVat';
import { useFeatureFlagValue } from '~/App/shared/featureFlags/hooks/useFeatureFlagValue';

const BuyDirectWrapper = styled(BodyText)`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 1rem;
`;

export interface BuyDirectProps {
  auction: Auction;
  priceText?: string;
  isRecreationalVehicle?: boolean;
  auctionType?: AuctionType;
}

const BuyDirect = ({
  auction,
  isRecreationalVehicle,
  priceText
}: BuyDirectProps) => {
  const { t } = useTranslation();
  const heavyEquipmentHighlightExVat = useFeatureFlagValue(
    'heavy-equipment-highlight-ex-vat'
  );
  const { buyNowAmount } = auction;
  const price = buyNowAmount ?? 0;

  const { isPriceReduced, initialPrice } = getReducedPrice(auction);

  const isHeavyEquipment = isHeavyEquipmentObject(auction.processObject);
  const showExclVat = useShowExclVat(auction?.processObject);
  const priceValue = isPriceReduced && initialPrice ? initialPrice : price;

  return (
    <BuyDirectWrapper as="div">
      <Flex gap={'0.5rem'} direction={'column'}>
        <BuyMethodPriceLabelValue
          price={
            heavyEquipmentHighlightExVat && showExclVat
              ? inclVATtoExclVAT(priceValue)
              : priceValue
          }
          currency={t('SEK')}
          title={`${priceText ? priceText : t('Buy direct')} ${
            heavyEquipmentHighlightExVat && showExclVat
              ? t('excl VAT')
              : t('incl VAT')
          }`}
          reducedPrice={
            isPriceReduced
              ? heavyEquipmentHighlightExVat && showExclVat
                ? inclVATtoExclVAT(price)
                : price
              : 0
          }
        />

        {showExclVat && (
          <BuyMethodPriceLabelValue
            price={
              heavyEquipmentHighlightExVat
                ? priceValue
                : inclVATtoExclVAT(priceValue)
            }
            currency={t('SEK')}
            title={`${priceText ? priceText : t('Buy direct')} ${
              heavyEquipmentHighlightExVat ? t('incl VAT') : t('excl VAT')
            }`}
            smallValue
            reducedPrice={
              isPriceReduced
                ? heavyEquipmentHighlightExVat
                  ? price
                  : inclVATtoExclVAT(price)
                : 0
            }
          />
        )}

        {!isRecreationalVehicle && !isHeavyEquipment && (
          <AlternativePaymentInformationBuyNow
            buyNowAmount={buyNowAmount ?? 0}
            withResidualValue={getAllowedResidualLoan(auction.processObject)}
          />
        )}

        <PriceInEUR
          title={t('Fixed price in EUR')}
          price={auction?.eurBuyNowAmount}
        />
      </Flex>
    </BuyDirectWrapper>
  );
};

export default memo(BuyDirect);
